import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  apiLogin,
  apiGoogleLogin,
  apiGetMe,
  setToken,
  clearToken,
  getToken,
  apiErrorMessage,
} from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, if we hold a token, validate it against the backend and hydrate
  // the user. An invalid/expired token is discarded.
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await apiGetMe();
        if (!cancelled) setUser(me);
      } catch (e) {
        clearToken();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    hydrate();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    try {
      const { token, user: u } = await apiLogin(email, password);
      setToken(token);
      setUser(u);
      return { success: true, user: u };
    } catch (e) {
      return { success: false, error: apiErrorMessage(e, 'Invalid email or password') };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const { token, user: u } = await apiGoogleLogin(credential);
      setToken(token);
      setUser(u);
      return { success: true, user: u };
    } catch (e) {
      return { success: false, error: apiErrorMessage(e, 'Google sign-in failed') };
    }
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  // Full access = everything (all dashboard pages + every section).
  // Today only admins qualify. When the paid tier ships, extend this to
  // `role === 'admin' || user?.isPaid` and viewers who pay get full access
  // without any routing changes.
  const isAdmin = user?.role === 'admin';
  const hasFullAccess = isAdmin;

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
    isAdmin,
    hasFullAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
